import "./SecurityPage.css";
import { FormEvent, useState } from "react";
import { changePasswordAsync } from "../../api/AccountApi";
import { getFieldValidationErrors } from "../../models/ValidationProblemDetails";
import Toast from "../../components/Toast";
import { usePasswordValidation } from "../../hooks/usePasswordValidation";
import { PASSWORD_POLICY_ERROR_MESSAGE } from "../../utils/passwordPolicy";
import PasswordRequirements from "../../components/PasswordRequirements";

interface ToastState {
  message: string;
  type: "success" | "error" | "info";
}

const SecurityPage = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const {
    checks,
    checklistId,
    isValid,
    touched,
    showError,
    handleBlur,
    reset,
  } = usePasswordValidation(newPassword);

  const handleChangePassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setFieldErrors({});

    if (!currentPassword || !newPassword || !confirmPassword) {
      setToast({ message: "All fields are required.", type: "error" });
      return;
    }

    handleBlur();

    if (!isValid) {
      setToast({ message: PASSWORD_POLICY_ERROR_MESSAGE, type: "error" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({ message: "New passwords do not match.", type: "error" });
      return;
    }

    if (newPassword === currentPassword) {
      setToast({
        message: "New password must be different from the current one.",
        type: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await changePasswordAsync({
        currentPassword,
        newPassword,
      });

      if (response.success) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setFieldErrors({});
        reset();
        setToast({ message: response.message || "Password updated successfully.", type: "success" });
      } else {
        setToast({ message: response.message || "Could not update password.", type: "error" });
        setFieldErrors({});
      }
    } catch (err) {
      const validationErrors = getFieldValidationErrors(err, {
        NewPassword: "newPassword",
        CurrentPassword: "currentPassword",
      });
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        setToast({ message: PASSWORD_POLICY_ERROR_MESSAGE, type: "error" });
      } else {
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? "Something went wrong. Please try again.";
        setToast({ message, type: "error" });
        setFieldErrors({});
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="security">
      <h2 className="security__title">Security</h2>
      <p className="security__subtitle">Manage your password.</p>

      <section className="security-card">
        <h3 className="security-card__title">Change Password</h3>
        <form className="security-form" onSubmit={handleChangePassword}>
          <label className="security-form__field">
            <span>Current Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </label>

          <label className="security-form__field">
            <span>New Password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              maxLength={128}
              aria-invalid={showError || !!fieldErrors.newPassword}
              aria-describedby={checklistId}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setFieldErrors((prev) => {
                  const next = { ...prev };
                  delete next.newPassword;
                  return next;
                });
              }}
            />
            <PasswordRequirements checks={checks} id={checklistId} />
            {(fieldErrors.newPassword || (showError && newPassword)) && (
              <p className="password-field-error" role="alert">
                {fieldErrors.newPassword ?? PASSWORD_POLICY_ERROR_MESSAGE}
              </p>
            )}
          </label>

          <label className="security-form__field">
            <span>Confirm New Password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </label>

          <button
            type="submit"
            className="security-btn"
            disabled={submitting}
          >
            {submitting ? "Updating..." : "Update Password"}
          </button>
        </form>
      </section>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default SecurityPage;
